import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CronogramaAtividadeAnexo } from '@/types';
import { cronogramaAnexoService } from '@/services/cronogramasApi';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { toast } from 'sonner';
import { ACCEPT_ATTR, formatBytes, validarAnexo } from './anexoConstants';

interface AnexosSectionProps {
  cronogramaId: string;
  atividadeId: string;
  anexos: CronogramaAtividadeAnexo[];
  onAnexosChange: (anexos: CronogramaAtividadeAnexo[]) => void;
}

interface PreviewState {
  anexo: CronogramaAtividadeAnexo;
  url: string;
}

export function AnexosSection({ cronogramaId, atividadeId, anexos, onAnexosChange }: AnexosSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const erro = validarAnexo(file);
    if (erro) {
      toast.error(erro);
      return;
    }

    setUploading(true);
    const res = await cronogramaAnexoService.upload(cronogramaId, atividadeId, file);
    setUploading(false);

    if (res.success) {
      toast.success(`"${file.name}" anexado com sucesso`);
      onAnexosChange([...anexos, res.data]);
    } else {
      toast.error(res.error || 'Erro ao enviar arquivo');
    }
  };

  const handleDelete = async (anexo: CronogramaAtividadeAnexo) => {
    setDeletingId(anexo.id);
    const res = await cronogramaAnexoService.delete(cronogramaId, atividadeId, anexo.id);
    setDeletingId(null);

    if (res.success) {
      toast.success(`"${anexo.nome_original}" removido`);
      onAnexosChange(anexos.filter(a => a.id !== anexo.id));
    } else {
      toast.error(res.error || 'Erro ao remover anexo');
    }
  };

  const handlePreview = async (anexo: CronogramaAtividadeAnexo) => {
    setBusyId(anexo.id);
    try {
      const blob = await cronogramaAnexoService.getConteudoBlob(cronogramaId, atividadeId, anexo.id);
      const url = URL.createObjectURL(blob);
      setPreview({ anexo, url });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao abrir o anexo');
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (anexo: CronogramaAtividadeAnexo) => {
    setBusyId(anexo.id);
    try {
      const blob = await cronogramaAnexoService.getConteudoBlob(cronogramaId, atividadeId, anexo.id);
      await saveOrShareBlob(blob, anexo.nome_original);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao baixar o anexo');
    } finally {
      setBusyId(null);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const isImage = preview?.anexo.mime_type?.startsWith('image/');
  const isPdf = preview?.anexo.mime_type === 'application/pdf';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Anexos {anexos.length > 0 && <Badge variant="secondary" className="ml-1">{anexos.length}</Badge>}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? 'Enviando...' : '+ Adicionar arquivo'}
        </Button>
      </div>

      <input ref={inputRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={handleFileChange} />
      <p className="text-xs text-muted-foreground">PDF, imagem ou documento Office · máx. 10 MB</p>

      {anexos.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">Nenhum anexo ainda.</p>
      ) : (
        <ul className="space-y-1.5">
          {anexos.map(anexo => (
            <li key={anexo.id} className="flex items-center gap-2 text-sm border rounded-md px-3 py-2 bg-muted/30">
              <button
                type="button"
                className="flex-1 truncate text-left hover:underline"
                title={`Visualizar ${anexo.nome_original}`}
                disabled={busyId === anexo.id}
                onClick={() => handlePreview(anexo)}
              >
                {busyId === anexo.id ? 'Abrindo...' : anexo.nome_original}
              </button>
              <span className="text-xs text-muted-foreground shrink-0">{formatBytes(anexo.tamanho_bytes)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                disabled={busyId === anexo.id}
                onClick={() => handleDownload(anexo)}
              >
                Baixar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                disabled={deletingId === anexo.id}
                onClick={() => handleDelete(anexo)}
              >
                {deletingId === anexo.id ? '...' : 'Remover'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!preview} onOpenChange={open => !open && closePreview()}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{preview?.anexo.nome_original}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="w-full">
              {isImage ? (
                <img src={preview.url} alt={preview.anexo.nome_original} className="max-h-[70vh] w-full object-contain rounded" />
              ) : isPdf ? (
                <iframe src={preview.url} title={preview.anexo.nome_original} className="w-full h-[70vh] rounded border" />
              ) : (
                <div className="py-10 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Pré-visualização não disponível para este tipo de arquivo.
                  </p>
                  <Button type="button" onClick={() => handleDownload(preview.anexo)}>Baixar arquivo</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
